/* @refresh skip */
import {
    type BaseTemplateArgs,
    flatten,
    resolveTemplate,
    translator,
} from "@solid-primitives/i18n";
import { makePersisted } from "@solid-primitives/storage";
import { type Pairs, getPairs } from "boltz-swaps/client";
import localforage from "localforage";
import log from "loglevel";
import {
    type Accessor,
    type JSX,
    type Setter,
    createContext,
    createEffect,
    createMemo,
    createSignal,
    useContext,
} from "solid-js";

import { config } from "../config";
import {
    type AssetType,
    LBTC,
    getAssetDisplaySymbol,
    getAssetNetwork,
    isBridgeAsset,
    isEvmAsset,
} from "../consts/Assets";
import { Denomination } from "../consts/Enums";
import { detectLanguage } from "../i18n/detect";
import dict, { type DictKey } from "../i18n/i18n";
import { type ECKeys, ECPair } from "../utils/ecpair";
import { formatError } from "../utils/errors";
import { getRegularReferral, isMobile } from "../utils/helper";
import { deleteOldLogs, injectLogWriter } from "../utils/logs";
import { migrateStorage } from "../utils/migration";
import { stringSerializer } from "../utils/persistence";
import {
    type RescueFile,
    deriveKey,
    deriveKeyGasAbstraction,
    generateRescueFile,
    getXpub,
} from "../utils/rescueFile";
import type { SomeSwap } from "../utils/swapCreator";
import { checkWasmSupported } from "../utils/wasmSupport";
import { detectWebLNProvider } from "../utils/webln";

export const liquidUncooperativeExtra = 3;

type NotificationType = "success" | "error";
export type deriveKeyFn = (index: number, asset: AssetType) => ECKeys;
export type newKeyFn = (
    asset: AssetType,
) => Promise<{ index: number; key: ECKeys }>;
export type tFn = (key: DictKey, values?: Record<string, unknown>) => string;
export type notifyFn = (type: NotificationType, message: string) => void;

export type GlobalContextType = {
    online: Accessor<boolean>;
    setOnline: Setter<boolean>;
    pairs: Accessor<Pairs | undefined>;
    setPairs: Setter<Pairs | undefined>;
    regularPairs: Accessor<Pairs | undefined>;
    setRegularPairs: Setter<Pairs | undefined>;
    wasmSupported: Accessor<boolean>;
    setWasmSupported: Setter<boolean>;
    refundAddress: Accessor<string | null>;
    setRefundAddress: Setter<string | null>;
    transactionToRefund: Accessor<string | null>;
    setTransactionToRefund: Setter<string | null>;
    i18n: Accessor<string | null>;
    setI18n: Setter<string | null>;
    notification: Accessor<string>;
    setNotification: Setter<string>;
    notificationType: Accessor<string>;
    setNotificationType: Setter<string>;
    webln: Accessor<boolean>;
    setWebln: Setter<boolean>;
    i18nConfigured: Accessor<string | null>;
    setI18nConfigured: Setter<string | null>;
    denomination: Accessor<Denomination>;
    setDenomination: Setter<Denomination>;
    hideHero: Accessor<boolean>;
    setHideHero: Setter<boolean>;
    separator: Accessor<string>;
    setSeparator: Setter<string>;
    settingsMenu: Accessor<boolean>;
    setSettingsMenu: Setter<boolean>;
    privacyMode: Accessor<boolean>;
    setPrivacyMode: Setter<boolean>;
    slippage: Accessor<number>;
    setSlippage: Setter<number>;
    gasTopUp: Accessor<boolean>;
    setGasTopUp: Setter<boolean>;
    zeroConf: Accessor<boolean>;
    setZeroConf: Setter<boolean>;
    bitcoinOnly: Accessor<boolean>;
    setBitcoinOnly: Setter<boolean>;
    embeddedMode: Accessor<boolean>;
    setEmbeddedMode: Setter<boolean>;
    parentOrigin: Accessor<string | undefined>;
    setParentOrigin: Setter<string | undefined>;
    // functions
    t: tFn;
    notify: notifyFn;
    fetchPairs: () => Promise<void>;
    fetchRegularPairs: () => Promise<void>;

    getLogs: () => Promise<Record<string, string[]>>;
    clearLogs: () => Promise<void>;

    setSwapStorage: (swap: SomeSwap) => Promise<void>;
    getSwap: <T = SomeSwap>(id: string) => Promise<T | null>;
    getSwaps: <T = SomeSwap>() => Promise<T[]>;
    deleteSwap: (id: string) => Promise<void>;
    clearSwaps: () => Promise<void>;
    updateSwapStatus: (id: string, newStatus: string) => Promise<boolean>;

    hardwareDerivationPath: Accessor<string>;
    setHardwareDerivationPath: Setter<string>;

    setRdns: (address: string, rdns: string) => Promise<string>;
    getRdnsAll: () => Promise<{ address: string; rdns: string }[]>;
    getRdnsForAddress: (address: string) => Promise<string | null>;

    newKey: newKeyFn;
    deriveKey: deriveKeyFn;
    deriveKeyGasAbstraction: (
        chainId: number,
        rescueFile?: RescueFile,
    ) => ECKeys;
    getXpub: () => string;
    setLastUsedKey: Setter<number>;
    getLastUsedEvmIndex: (currency: string) => Promise<number>;
    setLastUsedEvmIndex: (currency: string, value: number) => Promise<number>;
    clearLastUsedEvmIndex: () => Promise<void>;
    rescueFile: Accessor<RescueFile | null>;
    setRescueFile: Setter<RescueFile | null>;
    rescueFileBackupDone: Accessor<boolean>;
    setRescueFileBackupDone: Setter<boolean>;
};

const GlobalContext = createContext<GlobalContextType>();

const GlobalProvider = (props: {
    children: JSX.Element;
    initialEmbeddedMode?: boolean;
    initialParentOrigin?: string;
}) => {
    const [online, setOnline] = createSignal<boolean>(true);
    const [pairs, setPairs] = createSignal<Pairs | undefined>(undefined);
    const [regularPairs, setRegularPairs] = createSignal<Pairs | undefined>(
        undefined,
    );

    const [wasmSupported, setWasmSupported] = createSignal<boolean>(true);
    const [refundAddress, setRefundAddress] = createSignal<string | null>(null);

    const [transactionToRefund, setTransactionToRefund] = createSignal<
        string | null
    >(null);

    const [i18n, setI18n] = createSignal<string | null>(null);

    const [notification, setNotification] = createSignal<string>("");
    const [notificationType, setNotificationType] = createSignal<string>("");

    const [webln, setWebln] = createSignal<boolean>(false);

    const [hideHero, setHideHero] = createSignal<boolean>(false);

    const [i18nConfigured, setI18nConfigured] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<string | null>(null),
        {
            name: "i18n",
            ...stringSerializer,
        },
    );
    const [i18nUrl, setI18nUrl] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<string | null>(null),
        {
            name: "i18nUrl",
            ...stringSerializer,
        },
    );

    const [denomination, setDenomination] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<Denomination>(Denomination.Sat),
        {
            name: "denomination",
            ...stringSerializer,
        },
    );

    const [settingsMenu, setSettingsMenu] = createSignal<boolean>(false);

    const localeSeparator = (0.1).toLocaleString().charAt(1);
    const [separator, setSeparator] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal(localeSeparator),
        {
            name: "separator",
        },
    );

    const [rescueFile, setRescueFile] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<RescueFile | null>(null),
        {
            name: "rescueFile",
        },
    );

    const [lastUsedKey, setLastUsedKey] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<number>(0),
        {
            name: "lastUsedKey",
        },
    );

    const [rescueFileBackupDone, setRescueFileBackupDone] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<boolean>(false),
        {
            name: "rescueFileBackupDone",
        },
    );

    createEffect(() => {
        if (rescueFile() === null) {
            log.debug("Generating rescue file");
            setRescueFile(generateRescueFile());
        }
    });

    const deriveKeyWrapper = (index: number, asset: AssetType) => {
        const rf = rescueFile();
        if (rf === null) {
            throw new Error("rescue file is not initialised");
        }
        const derived = deriveKey(rf, index, asset);
        if (derived.privateKey === null) {
            throw new Error("derived private key is null");
        }
        return ECPair.fromPrivateKey(new Uint8Array(derived.privateKey));
    };

    const deriveKeyGasAbstractionWrapper = (
        chainId: number,
        rf?: RescueFile,
    ) => {
        const file = rf ?? rescueFile();
        if (file === null) {
            throw new Error("rescue file is not initialised");
        }
        const derived = deriveKeyGasAbstraction(file, chainId);
        if (derived.privateKey === null) {
            throw new Error("derived private key is null");
        }
        return ECPair.fromPrivateKey(new Uint8Array(derived.privateKey));
    };

    const newKey = async (asset: AssetType) => {
        if (isEvmAsset(asset)) {
            const index = await getLastUsedEvmIndex(asset);
            await setLastUsedEvmIndex(asset, index + 1);
            return {
                index,
                key: deriveKeyWrapper(index, asset),
            };
        }

        const index = lastUsedKey();
        setLastUsedKey(index + 1);
        return { index, key: deriveKeyWrapper(index, asset) };
    };

    const getXpubWrapper = () => {
        const rf = rescueFile();
        if (rf === null) {
            throw new Error("rescue file is not initialised");
        }
        return getXpub(rf);
    };

    const notify = (type: NotificationType, message: unknown) => {
        const messageStr = formatError(message, i18n());

        setNotificationType(type);
        setNotification(messageStr);
    };

    const addUncooperativeExtra = (pairs: Pairs) => {
        Object.values(pairs.chain).forEach((assetPairs) => {
            if (assetPairs[LBTC]) {
                assetPairs[LBTC].fees.minerFees.user.claim +=
                    liquidUncooperativeExtra;
            }
        });

        Object.values(pairs.reverse).forEach((assetPairs) => {
            if (assetPairs[LBTC]) {
                assetPairs[LBTC].fees.minerFees.claim +=
                    liquidUncooperativeExtra;
            }
        });
    };

    const fetchPairs = async () => {
        try {
            const data = await getPairs();

            addUncooperativeExtra(data);

            log.debug("getpairs", data);
            setOnline(true);
            setPairs(data);
        } catch (error) {
            log.error("Error fetching pairs", error);
            setOnline(false);
            throw formatError(error);
        }
    };

    const fetchRegularPairs = async () => {
        try {
            const data = await getPairs({
                headers: {
                    referral: getRegularReferral(),
                },
            });

            addUncooperativeExtra(data);

            log.debug("Regular pairs", data);
            setRegularPairs(data);
        } catch (error) {
            log.error("Error fetching regular pairs", error);
            throw formatError(error);
        }
    };

    // Use IndexedDB if available; fallback to LocalStorage
    localforage.config({
        driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
    });

    const logsForage = localforage.createInstance({
        name: "logs",
    });

    injectLogWriter(logsForage);
    log.info(`Version ${__APP_VERSION__}, commit hash ${__GIT_COMMIT__}`);

    createEffect(() => deleteOldLogs(logsForage));

    const getLogs = async () => {
        const logs: Record<string, string[]> = {};

        await logsForage.iterate<string[], unknown>((logArray, date) => {
            logs[date] = logArray;
        });

        return logs;
    };

    const clearLogs = () => logsForage.clear();

    const paramsForage = localforage.createInstance({
        name: "params",
    });
    const swapsForage = localforage.createInstance({
        name: "swaps",
    });

    migrateStorage(paramsForage, swapsForage).catch((e) =>
        log.error("Storage migration failed:", e),
    );

    const setSwapStorage = async (swap: SomeSwap) => {
        await swapsForage.setItem(swap.id, swap);
    };

    const deleteSwap = async (id: string) => await swapsForage.removeItem(id);

    const getSwap = <T = SomeSwap,>(id: string) => swapsForage.getItem<T>(id);

    const getSwaps = async <T = SomeSwap,>(): Promise<T[]> => {
        const swaps: T[] = [];

        await swapsForage.iterate<T, unknown>((swap) => {
            swaps.push(swap);
        });

        return swaps;
    };

    const updateSwapStatus = async (id: string, newStatus: string) => {
        const swap = await getSwap<SomeSwap & { status: string }>(id);

        if (swap === undefined || swap === null) {
            log.warn(`cannot update swap ${id} status: not found`);
            return false;
        }

        if (swap.status !== newStatus) {
            swap.status = newStatus;
            await setSwapStorage(swap);
            return true;
        }

        return false;
    };

    const clearSwaps = async () => {
        await swapsForage.clear();
    };

    const rdnsForage = localforage.createInstance({
        name: "rdns",
    });

    const setRdns = (address: string, rdns: string) =>
        rdnsForage.setItem(address.toLowerCase(), rdns);

    const lastUsedEvmIndexForage = localforage.createInstance({
        name: "lastUsedEvmIndex",
    });

    const getLastUsedEvmIndex = async (currency: string): Promise<number> => {
        const value = await lastUsedEvmIndexForage.getItem<number>(currency);
        return value ?? 0;
    };

    const setLastUsedEvmIndex = (currency: string, value: number) =>
        lastUsedEvmIndexForage.setItem(currency, value);

    const clearLastUsedEvmIndex = () => lastUsedEvmIndexForage.clear();

    const getRdnsAll = async () => {
        const result: { address: string; rdns: string }[] = [];

        await rdnsForage.iterate<string, unknown>((rdns, address) => {
            result.push({ address, rdns });
        });

        return result;
    };

    const getRdnsForAddress = (address: string) =>
        rdnsForage.getItem<string>(address.toLowerCase());

    setI18n(detectLanguage(i18nConfigured(), i18nUrl(), setI18nUrl));
    void detectWebLNProvider().then((state) => setWebln(state));
    setWasmSupported(checkWasmSupported());

    const [privacyMode, setPrivacyMode] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<boolean>(false),
        {
            name: "privacyMode",
        },
    );

    const [slippage, setSlippage] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<number>(0.01),
        {
            name: "slippage",
        },
    );

    const [gasTopUp, setGasTopUp] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<boolean>(true),
        {
            name: "gasTopUp",
        },
    );

    const [zeroConf, setZeroConf] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<boolean>(true),
        {
            name: "zeroConf",
        },
    );

    const [hardwareDerivationPath, setHardwareDerivationPath] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<string>(""),
        {
            name: "hardwareDerivationPath",
        },
    );

    const [bitcoinOnly, setBitcoinOnly] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal<boolean>(false),
        {
            name: "bitcoinOnly",
        },
    );

    const [embeddedMode, setEmbeddedMode] = createSignal<boolean>(
        props.initialEmbeddedMode ?? false,
    );

    const [parentOrigin, setParentOrigin] = createSignal<string | undefined>(
        props.initialParentOrigin,
    );

    createEffect(() => {
        if (isMobile()) {
            setZeroConf(true);
        }
    });

    // i18n
    createEffect(() => {
        setI18n(detectLanguage(i18nConfigured() || i18nUrl()));
    });
    const dictLocale = createMemo(
        () =>
            flatten(
                dict[(i18n() || config.defaultLanguage) as keyof typeof dict],
            ) as never,
    );

    const resolveDisplaySymbols = (
        template: string,
        values?: BaseTemplateArgs,
    ) => {
        if (typeof values?.asset === "string") {
            const raw = values.asset;
            let display = getAssetDisplaySymbol(raw);
            if (isBridgeAsset(raw)) {
                const network = getAssetNetwork(raw);
                if (network) {
                    display = `${display} (${network})`;
                }
            }
            values = { ...values, asset: display };
        }
        return resolveTemplate(template, values);
    };

    // eslint-disable-next-line solid/reactivity
    const t = translator(dictLocale, resolveDisplaySymbols) as unknown as tFn;

    return (
        <GlobalContext.Provider
            value={{
                online,
                setOnline,
                pairs,
                setPairs,
                regularPairs,
                setRegularPairs,
                wasmSupported,
                setWasmSupported,
                refundAddress,
                setRefundAddress,
                transactionToRefund,
                setTransactionToRefund,
                i18n,
                setI18n,
                notification,
                setNotification,
                notificationType,
                setNotificationType,
                webln,
                setWebln,
                i18nConfigured,
                setI18nConfigured,
                denomination,
                setDenomination,
                hideHero,
                setHideHero,
                separator,
                setSeparator,
                settingsMenu,
                setSettingsMenu,
                privacyMode,
                setPrivacyMode,
                slippage,
                setSlippage,
                gasTopUp,
                setGasTopUp,
                zeroConf,
                setZeroConf,
                bitcoinOnly,
                setBitcoinOnly,
                embeddedMode,
                setEmbeddedMode,
                parentOrigin,
                setParentOrigin,
                // functions
                t,
                notify,
                fetchPairs,
                fetchRegularPairs,
                getLogs,
                clearLogs,
                updateSwapStatus,
                setSwapStorage,
                getSwap,
                deleteSwap,
                getSwaps,
                clearSwaps,

                setRdns,
                getRdnsForAddress,
                getRdnsAll,
                hardwareDerivationPath,
                setHardwareDerivationPath,

                newKey,
                rescueFile,
                setRescueFile,
                setLastUsedKey,
                getLastUsedEvmIndex,
                setLastUsedEvmIndex,
                clearLastUsedEvmIndex,
                getXpub: getXpubWrapper,
                deriveKey: deriveKeyWrapper,
                deriveKeyGasAbstraction: deriveKeyGasAbstractionWrapper,

                rescueFileBackupDone,
                setRescueFileBackupDone,
            }}>
            {props.children}
        </GlobalContext.Provider>
    );
};

const useGlobalContext = () => {
    const context = useContext(GlobalContext);
    if (!context) {
        throw new Error("useGlobalContext: cannot find a GlobalContext");
    }
    return context;
};

export { useGlobalContext, GlobalProvider };
